import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { StatusBadge } from "../../components/ui/statusBadge";
import { Switch } from "../../components/ui/switch";
import { getErrorMessage } from "../../lib/query/errors";
import { useStipulations } from "./api/useStipulations";
import type { StipulationCategory } from "./stipulationCategoryFilters";
import { ALL_STIPULATION_CATEGORIES, CATEGORY_TO_WORKBENCH_SCREEN, STIPULATION_CATEGORIES } from "./stipulationCategoryFilters";
import { useStipulationCategoryStore } from "./stipulationCategoryStore";
import { useStipulationEditsStore } from "./stipulationEditsStore";
import StipulationRow from "./StipulationRow";

interface StipulationsPanelProperties {
  approvalId: string;
}

function StipulationsPanel({ approvalId }: StipulationsPanelProperties) {
  const { t } = useTranslation();
  const [openOnly, setOpenOnly] = useState(true);
  // Shared with the Signers card's scroll-spy (see useScrollSpy) — scrolling
  // through Income/Bank/Residence drives this same store, always overriding a
  // manual pick as soon as the analyst scrolls again.
  const category = useStipulationCategoryStore((state) => state.category);
  const setCategory = useStipulationCategoryStore((state) => state.setCategory);
  const resetCategory = useStipulationCategoryStore((state) => state.reset);
  const resetEdits = useStipulationEditsStore((state) => state.reset);

  // Drop pending, unsaved stip edits and any scroll-driven category filter
  // when the open deal changes so one deal's in-progress state never leaks
  // into another.
  useEffect(() => {
    resetEdits();
    resetCategory();
  }, [approvalId, resetEdits, resetCategory]);

  const { data, isLoading, isError, error } = useStipulations({ approvalId, openOnly });

  const items = data?.items ?? [];
  const openCount = data?.openCount ?? 0;

  const visible =
    category === ALL_STIPULATION_CATEGORIES
      ? items
      : items.filter((stip) => stip.workbenchScreens?.includes(CATEGORY_TO_WORKBENCH_SCREEN[category]));

  return (
    <section className="border-border bg-card flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border">
      <header className="border-border bg-section-header flex items-center justify-between border-b px-4 py-1.5">
        <h2 className="text-foreground text-sm font-semibold">{t("stipulations.heading")}</h2>
        {openCount > 0 && (
          <StatusBadge
            status="open"
            label={t("stipulations.openCount", { n: openCount })}
            className="border-success-strong text-success-strong"
          />
        )}
      </header>
      <div className="flex items-center justify-between gap-2 px-4 py-2">
        <Select
          value={category}
          onValueChange={(value) => setCategory(value as StipulationCategory | typeof ALL_STIPULATION_CATEGORIES)}
        >
          <SelectTrigger size="sm" aria-label={t("stipulations.filterCategory")} className="bg-card h-8 w-44 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {STIPULATION_CATEGORIES.map((code) => (
              <SelectItem key={code} value={code} className="text-xs">
                {code}
              </SelectItem>
            ))}
            <SelectItem value={ALL_STIPULATION_CATEGORIES} className="text-xs">
              {t("stipulations.filterAll")}
            </SelectItem>
          </SelectContent>
        </Select>
        <div className="flex items-center gap-2 text-xs">
          <label htmlFor="stips-open-only" className="text-muted-foreground">
            {t("stipulations.openOnly")}
          </label>
          <Switch
            id="stips-open-only"
            checked={openOnly}
            onCheckedChange={setOpenOnly}
            className="data-[state=checked]:bg-success-strong"
          />
        </div>
      </div>
      {isLoading && (
        <p role="status" className="text-muted-foreground px-4 py-3 text-xs">
          {t("stipulations.loading")}
        </p>
      )}
      {isError && (
        <p role="alert" className="text-destructive px-4 py-3 text-xs">
          {t("stipulations.loadError", { message: getErrorMessage(error) })}
        </p>
      )}
      {!isLoading && !isError && (
        <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto p-3">
          {visible.map((stip) => (
            <StipulationRow key={stip.id} approvalId={approvalId} stip={stip} />
          ))}
          {visible.length === 0 && (
            <p className="text-muted-foreground px-1 py-2 text-xs">
              {category === ALL_STIPULATION_CATEGORIES
                ? t("stipulations.empty")
                : t("stipulations.emptyCategory", { category })}
            </p>
          )}
        </div>
      )}
    </section>
  );
}

export default StipulationsPanel;
